"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { containerBreakpoints } from "../variants/interactions/responsiveVariants.js";
function computeBreakpoints(width, breakpoints) {
    const entries = Object.entries(breakpoints);
    const result = entries.reduce((acc, [key, min])=>{
        acc[key] = width >= min;
        return acc;
    }, {});
    return {
        ...result,
        width
    };
}
function hasBreakpointChanged(prev, next, breakpoints) {
    const currentKeys = Object.keys(breakpoints);
    const prevKeys = Object.keys(prev).filter((k)=>'width' !== k);
    if (currentKeys.length !== prevKeys.length || !currentKeys.every((k)=>prevKeys.includes(k))) return true;
    return currentKeys.some((breakpoint)=>prev[breakpoint] !== next[breakpoint]);
}
function useContainerBreakpoints(options) {
    const containerRef = useRef(null);
    const breakpointConfig = useMemo(()=>options?.breakpoints ? {
            ...containerBreakpoints,
            ...options.breakpoints
        } : containerBreakpoints, [
        options?.breakpoints
    ]);
    const [breakpoints, setBreakpoints] = useState(()=>computeBreakpoints(0, breakpointConfig));
    useEffect(()=>{
        const element = containerRef.current;
        if (!element) return;
        const resizeObserver = new ResizeObserver((entries)=>{
            const entry = entries[0];
            if (!entry) return;
            const newWidth = entry.contentRect.width;
            const newBreakpoints = computeBreakpoints(newWidth, breakpointConfig);
            setBreakpoints((prev)=>{
                if (hasBreakpointChanged(prev, newBreakpoints, breakpointConfig)) return newBreakpoints;
                if (prev.width !== newWidth) return {
                    ...prev,
                    width: newWidth
                };
                return prev;
            });
        });
        resizeObserver.observe(element);
        return ()=>{
            resizeObserver.disconnect();
        };
    }, [
        breakpointConfig
    ]);
    const value = useMemo(()=>({
            ...breakpoints,
            containerRef
        }), [
        breakpoints
    ]);
    return value;
}
export { useContainerBreakpoints };
